// frontend/src/pages/AssignPoints/RosterPointFlow.js
// Confirm & award points to ALL students in the selected roster.
// - Resolves habit/reason by name if IDs aren't provided
// - Fetches roster members via /api/rosters/:id*/students or fallback studentsApi.list({ roster_id })
// - Awards using bulk endpoint if available, else falls back to per-student POST
// - Refreshes leaderboard and routes to /dashboard on success
//
// Backend refactor notes:
// - Habits/Reasons are now served via GET /api/catalog/
// - Award endpoints are now:
//     POST /api/award/
//     POST /api/award-bulk/
// - Roster students endpoint is:
//     GET /api/rosters/<id>/students/
// - Django/DRF expects trailing slashes for POSTs.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { studentsApi } from "../../api/studentsApi";

export default function RosterPointFlow() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();

  const roster_id = sp.get("roster_id") || "";
  const roster_name = sp.get("roster_name") || "";
  const habit_name = sp.get("habit_name") || sp.get("habit") || "";
  const reason_text = sp.get("reason_text") || sp.get("reason") || "";
  const points_in = sp.get("points") || "1";

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [habitId, setHabitId] = useState(sp.get("habit_id") || "");
  const [reasonId, setReasonId] = useState(sp.get("reason_id") || "");

  const points = useMemo(() => Number(points_in) || 1, [points_in]);

  const summary = useMemo(
    () => [
      ["Target", roster_name ? `Roster: ${roster_name}` : (roster_id ? `Roster #${roster_id}` : "—")],
      ["Habit", habit_name || (habitId ? `#${habitId}` : "—")],
      ["Reason", reason_text || (reasonId ? `#${reasonId}` : "—")],
      ["Points", String(points)],
    ],
    [roster_name, roster_id, habit_name, reason_text, habitId, reasonId, points]
  );

  // Helper: normalize catalog habits array from backend shapes
  function extractHabitsFromCatalogData(data) {
    if (!data) return [];
    if (Array.isArray(data.habits)) return data.habits;
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  }

  // Resolve IDs when names given (catalog-based)
  useEffect(() => {
    let alive = true;
    (async () => {
      if (habitId && reasonId) return;
      if (!habit_name && !reason_text) return;

      setBusy(true);
      try {
        const catRes = await api.get("/api/catalog/");
        if (!catRes.ok) throw new Error("Catalog unavailable.");

        const habits = extractHabitsFromCatalogData(catRes.data);

        let hid = habitId;
        let habitObj = null;

        if (!hid && habit_name) {
          const hit =
            habits.find(h => (h.name || h.title) === habit_name) ||
            habits.find(h => String(h.id) === String(habit_name)) ||
            habits[0];

          hid = hit?.id ? String(hit.id) : "";
          habitObj = hit || null;
        } else if (hid) {
          habitObj = habits.find(h => String(h.id) === String(hid)) || null;
        }

        let rid = reasonId;
        if (!rid && reason_text) {
          const reasons =
            (habitObj && (habitObj.reasons || habitObj.reason_options || habitObj.reasonChoices)) ||
            [];

          const rHit =
            reasons.find(r => (r.text || r.name || r.title) === reason_text) ||
            reasons.find(r => String(r.id) === String(reason_text)) ||
            null;

          rid = rHit?.id ? String(rHit.id) : "";
        }

        if (alive) {
          setHabitId(hid || "");
          setReasonId(rid || "");
        }
      } catch {
        if (alive) setErr("Failed to resolve habit or reason.");
      } finally {
        if (alive) setBusy(false);
      }
    })();

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habit_name, reason_text]);

  async function fetchRosterStudents() {
    // Backend supports /api/rosters/<id>/students/
    // Keep defensive fallbacks for older/alternate shapes.
    const tries = roster_id ? [
      `/api/rosters/${roster_id}/students/`,
      `/api/rosters/${roster_id}/students`,  // fallback if someone deployed without slash
      `/api/groups/${roster_id}/students/`,
      `/api/groups/${roster_id}/students`,
      `/api/rosters/${roster_id}/`,
      `/api/rosters/${roster_id}`,
      `/api/groups/${roster_id}/`,
      `/api/groups/${roster_id}`,
    ] : [];

    for (const url of tries) {
      const res = await api.get(url);
      if (res && res.ok) {
        const arr = Array.isArray(res.data?.results)
          ? res.data.results
          : (Array.isArray(res.data?.students)
            ? res.data.students
            : (Array.isArray(res.data) ? res.data : []));
        if (arr.length) return arr;
      }
    }

    const listRes = await studentsApi.list({ roster_id });
    if (listRes.ok) return listRes.students;
    return [];
  }

  async function awardToIds(ids, hid, rid) {
    const items = ids.map(id => ({
      target_type: "student",
      student_id: String(id),
      habit_id: String(hid),
      reason_id: String(rid),
      points,
    }));

    // Try bulk endpoint first
    const bulk = await api.post("/api/award-bulk/", { items });
    if (bulk && bulk.ok) return;

    // Fall back to per-student
    for (const p of items) {
      const r = await api.post("/api/award/", p);
      if (!r.ok) throw new Error("One or more awards failed.");
    }
  }

  async function onConfirm() {
    setErr("");
    if (!(habitId || habit_name)) return setErr("Pick a habit.");
    if (!(reasonId || reason_text)) return setErr("Pick a reason.");
    if (!roster_id && !roster_name) return setErr("Pick a roster.");

    setBusy(true);
    try {
      const students = await fetchRosterStudents();
      const ids = students.map(s => s.id || s.scholar_id || s.student_id).filter(Boolean);
      if (ids.length === 0) throw new Error("No students in this roster.");

      const hid = habitId;
      const rid = reasonId;
      if (!hid || !rid) throw new Error("Habit or Reason not resolved.");

      await awardToIds(ids, hid, rid);

      await api.get("/api/leaderboard/").catch(() => {});
      navigate("/dashboard");
    } catch (e) {
      setErr(e?.message || "Award failed.");
    } finally {
      setBusy(false);
    }
  }

  const disabled = busy || !(habitId || habit_name) || !(reasonId || reason_text);

  return (
    <div style={s.page}>
      <h1 style={s.h1}>Confirm &amp; Award Points (Roster)</h1>

      <div style={s.card}>
        {summary.map(([k, v]) => (
          <div key={k} style={s.rowBetween}>
            <div style={s.label}>{k}</div>
            <div>{v}</div>
          </div>
        ))}
      </div>

      {err && <div style={s.error}>{err}</div>}

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={() => navigate(-1)} style={s.btnSecondary}>Back</button>
        <button onClick={onConfirm} disabled={disabled} style={s.btnPrimary}>
          {busy ? "Awarding…" : "Award Points to Roster"}
        </button>
      </div>
    </div>
  );
}

const s = {
  page: { padding: 20 },
  h1: { margin: "10px 0 16px", fontSize: 24, color: "#003e73" },
  card: {
    display: "grid",
    gap: 6,
    padding: 14,
    borderRadius: 10,
    border: "1px solid rgba(0,0,0,0.1)",
    background: "#fff",
    marginBottom: 12,
  },
  rowBetween: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  label: { fontWeight: 700, color: "#003e73" },
  btnPrimary: {
    height: 40, padding: "0 16px", borderRadius: 8, border: "none",
    background: "#2e7d32", color: "#fff", cursor: "pointer",
  },
  btnSecondary: {
    height: 40, padding: "0 16px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.2)",
    background: "#fff", color: "#333", cursor: "pointer",
  },
  error: {
    color: "#b00020",
    background: "#FFEAEA",
    border: "1px solid #F5B5B5",
    borderRadius: 8,
    padding: "8px 10px",
  },
};
